import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Burlington = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.burlington-on:About Burlington Paragraph 1">
            Nestled in Ontario, Burlington harmoniously merges natural
            tranquility with urban convenience, an attractive destination in the{' '}
            <LinkText href={cheapestLink}>Greater Toronto Area</LinkText>.
            Positioned along Lake Ontario’s shores, it offers breathtaking
            waterfront vistas and family-friendly neighborhoods, complemented by
            esteemed educational institutions and a strong community spirit.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.burlington-on:About Burlington Paragraph 2">
            Burlington’s allure lies in its balance of suburban comfort and
            access to Greater Toronto Area amenities. The dynamic downtown area
            boasts diverse shopping, dining, and cultural options, enriching
            residents’ daily lives. Whether enjoying serene lakeside ambiance or
            engaging in urban pursuits, living in Burlington,{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText> offers
            a blend of peaceful suburban living and easy city connectivity.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,900 - $2,600',
          bed2: '$2,300 - $3,300',
          bed3: '$3,000 - $4,000'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.burlington-on:Apartment Hunting in Burlington Paragraph 1">
            {{ cityName }} is an attractive city of Ontario known for its
            thriving community and diverse housing options. While rental prices
            in {{ cityName }} are generally more affordable compared to Toronto,
            they have been steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.under1000}>
              cheap rentals in {{ cityName }}
            </LinkText>
            , especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , can be challenging. However, to initiate your search, you can
            refer to this guide highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>{' '}
            that may offer relatively more budget-friendly rental options.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.burlington-on:Apartments for rent Burlington Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Burlington
              </LinkText>{' '}
              are a popular choice among young professionals, couples, and small
              families. The pricing for apartments can vary significantly, with
              higher costs for units in central locations or those with
              additional amenities. These are especially common in the city’s
              more urban areas where there’s a concentration of services and
              entertainment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.burlington-on:Houses for rent Burlington Paragraph">
              There are plenty of{' '}
              <LinkText href={links.house}>houses for rent</LinkText> in the
              suburban neighbourhoods of Burlington. It is typically more
              suitable for those with a higher budget, or who have multiple
              roommates. Both partial houses and entire houses for rent are
              common, as well as both new builds and older homes from the city’s
              earlier days.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.burlington-on:Townhouses for rent Burlington Paragraph">
              Townhouses are a popular form of housing in Burlington that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in Burlington
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance. It’s a popular
              option for small families and professionals.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.burlington-on:Condos for rent Burlington Paragraph">
              In more urban areas like Downtown Burlington,{' '}
              <LinkText href={links.apartment}>
                condos for rent in Burlington
              </LinkText>{' '}
              are a common sight. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.burlington-on:Short term rentals Burlington Paragraph">
              Burlington offers a variety of employment opportunities and is in
              proximity to several academic institutions in nearby cities,
              contributing to a demand for both long-term and short-term rental
              accommodations. While{' '}
              <LinkText href={links.shortTerm}>
                short-term rentals in Burlington
              </LinkText>{' '}
              can be sought after, especially during transitional periods like
              the beginning of school semesters or seasonal employment peaks,
              they aren’t the predominant rental type and are subject to local
              regulations. Nonetheless, the added flexibility is an appealing
              prospect for many renters, and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.burlington-on:Cheap rentals Burlington Paragraph">
              Compared to Toronto, Burlington, Ontario commands a lower rental
              price, making it slightly easier to find something within your
              budget. If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Burlington, ON
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in Burlington
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.burlington-on:Living in Burlington Paragraph 1">
            Living in Burlington,{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText>,
            presents a unique opportunity for residents to enjoy a blend of
            peaceful suburban life with the excitement of city amenities,
            proving ideal for families, young professionals, and even retirees.
            Strategically situated in the Greater Toronto Area, Burlington
            offers a renter-friendly market with a diverse array of housing
            options, ranging from luxurious condos to more budget-friendly
            apartments, accommodating various lifestyles and financial
            considerations. This proximity to{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>, a
            major Canadian metropolis, allows residents easy access to its vast
            array of cultural, educational, and professional opportunities,
            while still retreating to the quieter environs of Burlington.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.burlington-on:Living in Burlington Paragraph 2">
            The city itself is not without its attractions, boasting a stunning
            waterfront, the Royal Botanical Gardens, and numerous parks that
            beckon outdoor enthusiasts. Burlington is alive with events and
            activities, hosting the renowned Sound of Music Festival, Ribfest,
            and performances at the Burlington Performing Arts Centre,
            cultivating a strong sense of community and an appreciation for the
            arts. Known for its safe streets, high-quality schools, and cohesive
            neighborhoods, Burlington stands out as a highly desirable place to
            live within the dynamic expanse of the{' '}
            <LinkText href={cheapestLink}>Greater Toronto Area</LinkText>,
            offering a balanced lifestyle between the hustle of the big city and
            the comfort of a smaller community.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.burlington-on:Popular Neighbourhoods Burlington Paragraph">
          Like any city, Burlington is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Aldershot, Brant Hills, Headon Forest, Dynes, Maple, Tyandaga,
          Shoreacres, and Alton Village. For more info about Burlington, you can
          consult this guide on{' '}
          <LinkText href={neighborhoodsLink}>Toronto neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.burlington-on:Local Landmarks Burlington Paragraph">
          Burlington, Ontario, harbors several landmarks that capture its unique
          blend of natural beauty and cultural heritage. The Royal Botanical
          Gardens, one of the largest in North America, stands out as a lush
          paradise, inviting visitors to wander through diverse ecosystems,
          vibrant floral displays, and thematic gardens. Brant Street Pier, an
          iconic S-shaped walkway, extends into Lake Ontario at Spencer Smith
          Park, offering breathtaking views of the shoreline and is a popular
          spot for leisurely walks. The historic Ireland House at Oakridge Farm,
          a museum dedicated to Burlington’s agrarian roots, offers a glimpse
          into the life of early settlers through preserved architecture and
          artifacts. Additionally, the Burlington Art Gallery, renowned for its
          extensive collection of contemporary Canadian art, stands as a
          cultural cornerstone for art enthusiasts visiting the city.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Burlington
